// Plans/2026-10-19-vorab-bestand-plan.md §2.3: Plattform-Bestand = DR-Spiegel (`quantity`) + offene Vorab-Stueck.
// Kinder/Sets zeigen weiter nur den DR-Wert (Entscheidung §4.3).
import { TriangleAlert } from 'lucide-react'
import type { VorabEintrag } from '@/api/stock'

const zahl = (n: number) => n.toLocaleString('de-DE')
const datum = (iso: string) => new Date(iso).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' })

/** Eine Zeile je offenem Vorab: „WE 12 · 20.10.26, 14:03 · Max: 40 Stück“. */
export function vorabHerkunft(e: VorabEintrag): string {
  const wer = e.created_by_name ? ` · ${e.created_by_name}` : ''
  const doppelt = e.evtl_doppelt ? ` · DR seit Vorab +${zahl(e.dr_plus ?? 0)}, evtl. doppelt` : ''
  return `WE ${e.receipt_id} · ${datum(e.created_at)}${wer}: ${zahl(e.stueck)} Stück vorab${doppelt}`
}

interface Props {
  quantity: number
  vorabOffen?: number
  vorabEintraege?: VorabEintrag[]
  /** Kind/Set: nur DR-Wert, ausdruecklich als „DR“ beschriftet. */
  nurDr?: boolean
  /** `kopf` = grosse Zahl im Grundartikel-Kopf, `zelle` = Katalogzeile,
   *  `kurz` = eine Zeile fuer den festen Kopf (Aufteilung nur im Tooltip). */
  variante?: 'kopf' | 'zelle' | 'kurz'
}

export function BestandMitVorab({ quantity, vorabOffen = 0, vorabEintraege = [], nurDr = false, variante = 'zelle' }: Props) {
  if (nurDr) {
    return <span className="font-mono" title="Wert aus DreamRobot">{zahl(quantity)} <span className="text-[10px] text-ink-2">DR</span></span>
  }
  if (vorabOffen <= 0) return <span className="font-mono">{zahl(quantity)}</span>

  const gesamt = quantity + vorabOffen
  const doppelt = vorabEintraege.some((e) => e.evtl_doppelt)
  const herkunft = vorabEintraege.map(vorabHerkunft).join('\n')
  const aufteilung = `${zahl(quantity)} DR + ${zahl(vorabOffen)} vorab`
  const label = `Bestand ${zahl(gesamt)} (${aufteilung})`

  if (variante === 'kurz') {
    return (
      <span className="inline-flex items-center gap-1 whitespace-nowrap" title={`${aufteilung}
${herkunft}`} aria-label={label} data-testid="bestand-mit-vorab">
        <span className="font-mono">{zahl(gesamt)}</span>
        <span className="rounded bg-panel-2 px-1 font-mono text-[10px] font-medium text-ink-2">+{zahl(vorabOffen)} vorab</span>
        {doppelt && (
          <span title="Evtl. doppelt gezählt: DR ist seit dem Vorab gestiegen" className="text-amber-700">
            <TriangleAlert className="size-3.5" aria-hidden />
          </span>
        )}
      </span>
    )
  }
  if (variante === 'kopf') {
    return (
      <div className="text-right" data-testid="bestand-mit-vorab">
        <div className="font-display font-bold text-2xl font-mono text-ink" aria-label={label}>{zahl(gesamt)}</div>
        <div className="text-[11px] text-ink-2 font-mono">({aufteilung})</div>
        <ul className="mt-1 space-y-0.5 text-[11px] text-ink-2">
          {vorabEintraege.map((e) => (
            <li key={e.id} className={e.evtl_doppelt ? 'text-amber-700 font-medium' : undefined}>{vorabHerkunft(e)}</li>
          ))}
        </ul>
      </div>
    )
  }
  return (
    <span className="inline-flex flex-col items-end leading-tight" title={herkunft} aria-label={label} data-testid="bestand-mit-vorab">
      <span className="font-mono">{zahl(gesamt)}</span>
      <span className={`text-[10px] font-mono ${doppelt ? 'text-amber-700 font-semibold' : 'text-ink-2'}`}>
        {aufteilung}{doppelt ? ' · evtl. doppelt' : ''}
      </span>
    </span>
  )
}
